import {
  ABILITIES,
  AUTO_ATTACKS,
  ArenaState,
  AbilityId,
  BuffType,
  canAutoAttack,
  canUseAbility,
  ClassType,
  ClientMessageType,
  CombatEvent,
  CombatEventMessage,
  CombatEventType,
  GameMode,
  getClassAbilities,
  getClassTalents,
  LeaderboardEntry,
  LobbyStatusMessage,
  LoginMessage,
  LoginOkMessage,
  MatchEndMessage,
  MatchPhase,
  MatchStartMessage,
  PlayerData,
  PlayerState,
  QueueUpdateMessage,
  StateUpdateMessage,
  TargetType,
  ServerMessageType,
} from '@pvp/shared';
import { InputManager } from './input/index.js';
import { NetworkManager } from './network/index.js';
import { Renderer } from './renderer/index.js';

const SESSION_TOKEN_KEY = 'pvp_session_token';
const SESSION_USERNAME_KEY = 'pvp_session_username';
const SELECTED_CLASS_KEY = 'pvp_selected_class';
const BOT_FILL_KEY = 'pvp_allow_bot_fill';
type AuthMode = 'login' | 'register';

let myPlayerId = '';
let myPlayerData: PlayerData | null = null;
let currentState: ArenaState | null = null;
let selectedClass: ClassType = loadStoredClass();
let allowBotFill = loadStoredBotFill();
let authMode: AuthMode = 'login';
let inMatch = false;
let inQueue = false;
let toastTimer: ReturnType<typeof setTimeout> | null = null;
let lastTouchAbilityUseAt = 0;
let authBusy = false;
let sessionRestoreInFlight = false;
let pendingCredentialLogin: LoginMessage | null = null;
const isTouchDevice = window.matchMedia('(pointer: coarse)').matches || navigator.maxTouchPoints > 0;

document.body.classList.toggle('touch-device', isTouchDevice);

(document.getElementById('version-label') as HTMLElement).textContent = `v${__APP_VERSION__}`;

const loginScreen = document.getElementById('login-screen')!;
const lobbyScreen = document.getElementById('lobby-screen')!;
const hud = document.getElementById('hud')!;
const usernameInput = document.getElementById('username-input') as HTMLInputElement;
const passwordInput = document.getElementById('password-input') as HTMLInputElement;
const loginBtn = document.getElementById('login-btn') as HTMLButtonElement;
const createAccountBtn = document.getElementById('create-account-btn') as HTMLButtonElement;
const authSwitchText = document.getElementById('auth-switch-text')!;
const authTitle = document.getElementById('auth-title')!;
const authCopy = document.getElementById('auth-copy')!;
const authNote = document.getElementById('auth-note')!;
const registerFields = document.getElementById('register-fields')!;
const classBtns = Array.from(document.querySelectorAll<HTMLElement>('.class-btn'));
const countdownOverlay = document.getElementById('countdown-overlay')!;
const matchResult = document.getElementById('match-result')!;
const resultText = document.getElementById('result-text')!;
const resultStats = document.getElementById('result-stats')!;
const resultBack = document.getElementById('result-back')!;
const playerInfo = document.getElementById('lobby-player-info')!;
const playerStats = document.getElementById('player-stats')!;
const queueSpinner = document.getElementById('queue-spinner')!;
const queue1v1Btn = document.getElementById('queue-1v1') as HTMLButtonElement;
const queue2v2Btn = document.getElementById('queue-2v2') as HTMLButtonElement;
const botFillToggle = document.getElementById('bot-fill-toggle') as HTMLInputElement;
const logoutBtn = document.getElementById('logout-btn') as HTMLButtonElement;
const loginShowClassKitBtn = document.getElementById('login-show-class-kit') as HTMLButtonElement;
const showClassKitBtn = document.getElementById('show-class-kit') as HTMLButtonElement;
const leaderboardPanel = document.getElementById('leaderboard-panel')!;
const leaderboardBody = document.getElementById('lb-body')!;
const classKitPanel = document.getElementById('class-kit-panel')!;
const classKitModalTitle = document.getElementById('class-kit-modal-title')!;
const classKitModalSubtitle = document.getElementById('class-kit-modal-subtitle')!;
const classKitModalList = document.getElementById('class-kit-modal-list')!;
const loginClassPreview = document.getElementById('login-class-preview')!;
const onlineCount = document.getElementById('online-count')!;
const queue1v1Count = document.getElementById('queue-1v1-count')!;
const queue2v2Count = document.getElementById('queue-2v2-count')!;
const presenceTableBody = document.getElementById('presence-table-body')!;
const toast = document.getElementById('status-toast')!;
const abilityBar = document.getElementById('ability-bar')!;
const mobileMovePad = document.getElementById('mobile-move-pad') as HTMLElement;
const mobileMoveThumb = document.getElementById('mobile-move-thumb') as HTMLElement;
const mobileLookPad = document.getElementById('mobile-look-pad') as HTMLElement;
const mobileTargetEnemy = document.getElementById('mobile-target-enemy') as HTMLButtonElement;
const mobileTargetAlly = document.getElementById('mobile-target-ally') as HTMLButtonElement;
const mobileTargetSelf = document.getElementById('mobile-target-self') as HTMLButtonElement;
const mobileCancelCast = document.getElementById('mobile-cancel-cast') as HTMLButtonElement;
const mobileTargetStatus = document.getElementById('mobile-target-status')!;
const selfAutoAttackStatus = document.getElementById('self-auto-attack-status')!;

const canvas = document.createElement('canvas');
canvas.style.position = 'absolute';
canvas.style.top = '0';
canvas.style.left = '0';
canvas.hidden = true;
document.getElementById('app')!.prepend(canvas);

const net = new NetworkManager();
const renderer = new Renderer(canvas);
const input = new InputManager(net, canvas);

if (isTouchDevice) {
  input.bindMobileControls({
    movePad: mobileMovePad,
    moveThumb: mobileMoveThumb,
    lookPad: mobileLookPad,
  });
}

input.setTabTargetHandler((direction) => {
  if (isTouchDevice) return;
  cycleEnemyTarget(direction);
});

const classLabels: Record<ClassType, string> = {
  [ClassType.Warrior]: 'Warrior',
  [ClassType.Mage]: 'Mage',
  [ClassType.Priest]: 'Priest',
  [ClassType.Paladin]: 'Paladin',
};

const classDescriptions: Record<ClassType, string> = {
  [ClassType.Warrior]: 'Pressure target, stick to opponents, and punish low health with Execute.',
  [ClassType.Mage]: 'Control space with roots and polymorph while kiting for burst windows.',
  [ClassType.Priest]: 'Win long fights with shields, self-heals, damage-over-time, and panic fear.',
  [ClassType.Paladin]: 'Versatile holy warrior: melee burst, healing, stun CC, and a life-saving immunity bubble.',
};

const classIcons: Record<ClassType, string> = {
  [ClassType.Warrior]: '&#x2694;',
  [ClassType.Mage]: '&#x2728;',
  [ClassType.Priest]: '&#x271D;',
  [ClassType.Paladin]: '&#x269C;',
};

const abilityIcons: Record<string, string> = {
  charge: '&#x1F3C3;',
  mortal_strike: '&#x2694;',
  shield_block: '&#x1F6E1;',
  execute: '&#x1F4A5;',
  hamstring: '&#x1F9B5;',
  fireball: '&#x1F525;',
  frost_nova: '&#x2744;',
  blink: '&#x26A1;',
  polymorph: '&#x1F411;',
  ice_barrier: '&#x1F9CA;',
  counterspell: '&#x1F6AB;',
  crusader_strike: '&#x2694;',
  hammer_of_justice: '&#x1F528;',
  flash_of_light: '&#x1FA79;',
  divine_shield: '&#x1F6E1;',
  consecration: '&#x2600;',
  heal: '&#x2764;',
  shadow_word_pain: '&#x1F480;',
  power_word_shield: '&#x1F6E1;',
  smite: '&#x2600;',
  psychic_scream: '&#x1F631;',
};

const classButtonMap = new Map<ClassType, HTMLElement>(
  classBtns.map((button) => [button.dataset.class as ClassType, button]),
);

classBtns.forEach((button) => {
  button.addEventListener('click', () => {
    setSelectedClass(button.dataset.class as ClassType, false);
  });
});

usernameInput.addEventListener('input', updateLoginButton);
passwordInput.addEventListener('input', updateLoginButton);
botFillToggle.checked = allowBotFill;
botFillToggle.addEventListener('change', () => {
  allowBotFill = botFillToggle.checked;
  localStorage.setItem(BOT_FILL_KEY, String(allowBotFill));
});
passwordInput.addEventListener('keydown', (event) => {
  if (event.key === 'Enter' && !loginBtn.disabled) {
    submitLogin(authMode === 'register');
  }
});
usernameInput.addEventListener('keydown', (event) => {
  if (event.key === 'Enter' && !loginBtn.disabled) {
    submitLogin(authMode === 'register');
  }
});

abilityBar.addEventListener('pointerdown', (event) => {
  if (!(event instanceof PointerEvent) || event.pointerType === 'mouse') return;

  const abilityButton = getAbilityButtonFromEventTarget(event.target);
  if (!abilityButton) return;

  lastTouchAbilityUseAt = performance.now();
  event.preventDefault();
  event.stopPropagation();
  sendAbilityFromButton(abilityButton);
});

abilityBar.addEventListener('click', (event) => {
  const abilityButton = getAbilityButtonFromEventTarget(event.target);
  if (!abilityButton) return;
  if (performance.now() - lastTouchAbilityUseAt < 500) return;

  event.preventDefault();
  sendAbilityFromButton(abilityButton);
});

mobileTargetEnemy.addEventListener('click', () => {
  selectNearestTarget('enemy');
});

mobileTargetAlly.addEventListener('click', () => {
  selectNearestTarget('ally');
});

mobileTargetSelf.addEventListener('click', () => {
  setCurrentTarget(myPlayerId || null);
});

mobileCancelCast.addEventListener('click', () => {
  net.send({ type: ClientMessageType.CancelCast });
});

loginBtn.addEventListener('click', () => {
  submitLogin(authMode === 'register');
});

createAccountBtn.addEventListener('click', () => {
  setAuthMode(authMode === 'login' ? 'register' : 'login');
});

logoutBtn.addEventListener('click', () => {
  performLogout();
});

queue1v1Btn.addEventListener('click', () => {
  if (inQueue) {
    net.send({ type: ClientMessageType.QueueLeave });
    return;
  }

  net.send({
    type: ClientMessageType.QueueJoin,
    mode: GameMode.Arena1v1,
    allowBotFill,
  });
});

queue2v2Btn.addEventListener('click', () => {
  if (inQueue) {
    net.send({ type: ClientMessageType.QueueLeave });
    return;
  }

  net.send({
    type: ClientMessageType.QueueJoin,
    mode: GameMode.Arena2v2,
    allowBotFill,
  });
});

document.getElementById('show-leaderboard')!.addEventListener('click', () => {
  leaderboardPanel.style.display = 'flex';
  void loadLeaderboard();
});

loginShowClassKitBtn.addEventListener('click', () => {
  showClassKitPanel();
});

showClassKitBtn.addEventListener('click', () => {
  showClassKitPanel();
});

net.onOpen(() => {
  if (pendingCredentialLogin) {
    net.send(pendingCredentialLogin);
    return;
  }

  const storedSession = getStoredSession();
  if (!storedSession) {
    return;
  }

  sessionRestoreInFlight = true;
  authBusy = true;
  updateLoginButton();
  net.send({
    type: ClientMessageType.Login,
    username: storedSession.username,
    sessionToken: storedSession.sessionToken,
    classType: selectedClass,
  });
});

net.onClose(() => {
  // If we were mid-session-restore or mid-login and the connection dropped,
  // re-enable the form so the user isn't stuck with disabled inputs.
  if (authBusy && !pendingCredentialLogin) {
    authBusy = false;
    updateLoginButton();
  }
});

document.getElementById('lb-close')!.addEventListener('click', () => {
  leaderboardPanel.style.display = 'none';
});

document.getElementById('show-talents')!.addEventListener('click', () => {
  showTalentsPanel();
});

document.getElementById('talents-close')!.addEventListener('click', () => {
  document.getElementById('talents-panel')!.style.display = 'none';
});

document.getElementById('class-kit-close')!.addEventListener('click', () => {
  classKitPanel.style.display = 'none';
});

resultBack.addEventListener('click', async () => {
  matchResult.style.display = 'none';
  hud.style.display = 'none';
  canvas.hidden = true;
  currentState = null;
  renderer.clearCharacters();
  updateMobileTargetStatus();
  await refreshPlayerProfile();
  if (!myPlayerData) {
    return;
  }
  showLobby();
});

net.on(ServerMessageType.LoginOk, (msg) => {
  const data = msg as LoginOkMessage;
  myPlayerId = data.playerId;
  myPlayerData = data.player;
  pendingCredentialLogin = null;
  sessionRestoreInFlight = false;
  authBusy = false;
  saveSession(data.player.name, data.sessionToken);
  usernameInput.value = data.player.name;
  passwordInput.value = '';
  setAuthMode('login');
  updateLoginButton();
  setSelectedClass(data.player.classType, true);
  loginScreen.style.display = 'none';
  showLobby();
  void refreshPlayerProfile();
});

net.on(ServerMessageType.Error, (msg) => {
  const message = (msg as { message: string }).message;
  pendingCredentialLogin = null;
  authBusy = false;
  updateLoginButton();

  if (sessionRestoreInFlight) {
    sessionRestoreInFlight = false;
    clearStoredSession();
    resetUiForLogin();
    net.disconnect();
  }

  console.error('Server error:', message);
  showToast(message, 'error');
});

net.on(ServerMessageType.QueueUpdate, (msg) => {
  const data = msg as QueueUpdateMessage;
  inQueue = data.queued;
  updateQueueUI(data);
});

net.on(ServerMessageType.LobbyStatus, (msg) => {
  updateLobbyPresence(msg as LobbyStatusMessage);
});

net.on(ServerMessageType.MatchStart, (msg) => {
  const data = msg as MatchStartMessage;

  inMatch = true;
  inQueue = false;
  lobbyScreen.style.display = 'none';
  hud.style.display = 'block';
  matchResult.style.display = 'none';
  leaderboardPanel.style.display = 'none';
  classKitPanel.style.display = 'none';
  document.getElementById('talents-panel')!.style.display = 'none';
  canvas.hidden = false;

  const activeClass = getActiveClass();
  const abilities = getClassAbilities(activeClass);
  setupAbilityBar(abilities);
  input.setAbilityKeys(abilities.map((ability) => ability.id));
  input.setTargetClickHandler((event) => {
    if (!currentState) return;
    const targetId = renderer.getClickedPlayerId(event, currentState, myPlayerId);
    setCurrentTarget(targetId);
  });

  const controlsMessage = isTouchDevice
    ? 'Move with left pad, drag right to look, tap to target.'
    : 'Click a player to target. Friendly priest spells self-cast when no ally is selected.';
  showToast(
    data.practice
      ? `No opponent found. Practice match vs easy bot starting. ${controlsMessage}`
      : controlsMessage,
    'info',
  );
  updateQueueUI({ queued: false, mode: null, estimatedWait: 0, type: ServerMessageType.QueueUpdate });
  updateMobileTargetStatus();
});

net.on(ServerMessageType.StateUpdate, (msg) => {
  const data = msg as StateUpdateMessage;
  currentState = data.state;
  updateMobileTargetStatus();

  if (currentState.phase === MatchPhase.Countdown) {
    countdownOverlay.style.display = 'flex';
    countdownOverlay.textContent = currentState.countdown > 0 ? String(currentState.countdown) : 'FIGHT';
    return;
  }

  countdownOverlay.style.display = 'none';
});

net.on(ServerMessageType.CombatEvent, (msg) => {
  const data = msg as CombatEventMessage;
  for (const event of data.events) {
    handleCombatEvent(event);
  }
});

net.on(ServerMessageType.MatchEnd, (msg) => {
  const data = msg as MatchEndMessage;
  const draw = data.winnerTeam === -1;
  const myTeam = currentState?.players[myPlayerId]?.team;
  const won = !draw && myTeam === data.winnerTeam;
  const myRating = data.ratingChanges[myPlayerId];
  const myCurrency = data.currencyEarned[myPlayerId] || 0;
  const myStats = data.stats[myPlayerId] || { damage: 0, healing: 0, kills: 0, deaths: 0 };
  const ratingChange = myRating ? myRating.after - myRating.before : 0;
  const ratingSign = ratingChange >= 0 ? '+' : '';

  inMatch = false;
  if (currentState?.players[myPlayerId]) {
    currentState.players[myPlayerId].targetId = null;
    currentState.players[myPlayerId].autoAttackTargetId = null;
  }
  resultText.textContent = draw ? 'DRAW' : (won ? 'VICTORY' : 'DEFEAT');
  resultText.className = draw ? 'draw' : (won ? 'victory' : 'defeat');
  resultStats.innerHTML = `
    <div class="result-grid">
      <div class="result-card"><span>Rating</span><strong>${myRating?.after ?? '?'}</strong><small>${data.practice ? 'practice - unchanged' : `${ratingSign}${ratingChange}`}</small></div>
      <div class="result-card"><span>Currency</span><strong>+${myCurrency}</strong><small>${data.practice ? 'bot match payout disabled' : 'match payout'}</small></div>
      <div class="result-card"><span>Damage</span><strong>${formatCompact(myStats.damage)}</strong><small>total dealt</small></div>
      <div class="result-card"><span>Healing</span><strong>${formatCompact(myStats.healing)}</strong><small>total restored</small></div>
      <div class="result-card"><span>Kills</span><strong>${myStats.kills}</strong><small>final blows</small></div>
      <div class="result-card"><span>Deaths</span><strong>${myStats.deaths}</strong><small>round total</small></div>
    </div>
  `;
  matchResult.style.display = 'flex';

  if (myPlayerData && myRating && !data.practice) {
    myPlayerData.rating = myRating.after;
    myPlayerData.bestRating = Math.max(myPlayerData.bestRating, myRating.after);
    myPlayerData.currency += myCurrency;
    myPlayerData.matchesPlayed += 1;
    myPlayerData.totalDamage += myStats.damage;
    myPlayerData.totalHealing += myStats.healing;
    myPlayerData.totalKills += myStats.kills;
    myPlayerData.totalDeaths += myStats.deaths;

    if (!draw) {
      if (won) {
        myPlayerData.wins += 1;
      } else {
        myPlayerData.losses += 1;
      }
    }
  }

  updateLobbyUI();
  updateMobileTargetStatus();
  void refreshPlayerProfile();
});

function setSelectedClass(classType: ClassType, preserveNameFocus: boolean): void {
  selectedClass = classType;
  localStorage.setItem(SELECTED_CLASS_KEY, classType);
  syncSelectedClassButtons(classType);
  renderClassPreview(classType);
  updateAuthModeUI();
  updateLoginButton();

  if (!preserveNameFocus && authMode === 'register') {
    usernameInput.focus();
  }
}

function syncSelectedClassButtons(classType: ClassType): void {
  for (const [buttonClass, button] of classButtonMap) {
    button.classList.toggle('selected', buttonClass === classType);
  }
}

function updateLoginButton(): void {
  const hasName = usernameInput.value.trim().length > 0;
  const hasPassword = passwordInput.value.length > 0;
  const hasClass = document.querySelector('.class-btn.selected') !== null;
  const ready = hasName && hasPassword && (authMode === 'login' || hasClass) && !authBusy;
  loginBtn.disabled = !ready;
  usernameInput.disabled = authBusy;
  passwordInput.disabled = authBusy;
  createAccountBtn.disabled = authBusy;
}

function setAuthMode(mode: AuthMode): void {
  authMode = mode;
  updateAuthModeUI();
  updateLoginButton();
}

function updateAuthModeUI(): void {
  const isRegisterMode = authMode === 'register';
  registerFields.hidden = !isRegisterMode;
  authTitle.textContent = isRegisterMode ? 'Create Account' : 'Sign In';
  authCopy.textContent = isRegisterMode
    ? 'Choose a class once for the new account, then create credentials and enter the lobby.'
    : 'Unlock an existing account with username and password. Class selection is only used when creating a new account.';
  authNote.textContent = isRegisterMode
    ? 'New accounts are class-locked on creation. The browser keeps the session active until logout.'
    : 'The browser keeps the session active until logout. Switching class is not part of sign-in.';
  passwordInput.autocomplete = isRegisterMode ? 'new-password' : 'current-password';
  loginBtn.textContent = isRegisterMode ? 'Create Account' : 'Sign In';
  authSwitchText.textContent = isRegisterMode ? 'Already have an account?' : "Don't have an account?";
  createAccountBtn.textContent = isRegisterMode ? 'Sign in' : 'Create one';
  loginShowClassKitBtn.hidden = !isRegisterMode;

  if (isRegisterMode) {
    renderClassPreview(selectedClass);
    return;
  }

  loginClassPreview.innerHTML = `
    <div class="class-preview-card account-preview-card">
      <div class="class-preview-head">
        <span class="class-preview-icon">&#x1F512;</span>
        <div class="class-preview-copy">
          <h3>Account Access</h3>
          <p>Sign in first. After authentication, the lobby, queues, talents, and leaderboard unlock.</p>
        </div>
      </div>
      <div class="class-preview-tags">
        <span class="preview-pill">Username + password</span>
        <span class="preview-pill">Session persisted</span>
        <span class="preview-pill">Class locked on account</span>
      </div>
      <div class="class-preview-abilities">
        <article class="summary-ability">
          <span>&#x1F3F9;</span>
          <strong>Queue after login</strong>
        </article>
        <article class="summary-ability">
          <span>&#x1F4CA;</span>
          <strong>Stats in lobby</strong>
        </article>
        <article class="summary-ability">
          <span>&#x1F3C6;</span>
          <strong>Ranked leaderboard</strong>
        </article>
      </div>
      <p class="class-preview-tip">Use Create New Account only when you need a fresh account and class.</p>
    </div>
  `;
}

function getActiveClass(): ClassType {
  return myPlayerData?.classType ?? selectedClass;
}

function submitLogin(createAccount: boolean): void {
  const username = usernameInput.value.trim();
  const password = passwordInput.value;
  if (!username || !password || authBusy) {
    return;
  }

  pendingCredentialLogin = {
    type: ClientMessageType.Login,
    username,
    password,
    classType: selectedClass,
    createAccount,
  };
  sessionRestoreInFlight = false;
  authBusy = true;
  updateLoginButton();

  if (net.connected) {
    net.send(pendingCredentialLogin);
    return;
  }

  net.connect();
}

function performLogout(): void {
  pendingCredentialLogin = null;
  sessionRestoreInFlight = false;
  authBusy = false;
  clearStoredSession();
  resetUiForLogin();

  if (net.connected) {
    net.send({ type: ClientMessageType.Logout });
  }
  net.disconnect();
}

function resetUiForLogin(): void {
  myPlayerId = '';
  myPlayerData = null;
  currentState = null;
  inMatch = false;
  inQueue = false;
  canvas.hidden = true;
  hud.style.display = 'none';
  lobbyScreen.style.display = 'none';
  matchResult.style.display = 'none';
  leaderboardPanel.style.display = 'none';
  classKitPanel.style.display = 'none';
  document.getElementById('talents-panel')!.style.display = 'none';
  renderer.clearCharacters();
  updateQueueUI({ queued: false, mode: null, estimatedWait: 0, type: ServerMessageType.QueueUpdate });
  updateMobileTargetStatus();
  setAuthMode('login');
  passwordInput.value = '';
  loginScreen.style.display = 'flex';
}

function saveSession(username: string, sessionToken: string): void {
  localStorage.setItem(SESSION_USERNAME_KEY, username);
  localStorage.setItem(SESSION_TOKEN_KEY, sessionToken);
}

function clearStoredSession(): void {
  localStorage.removeItem(SESSION_USERNAME_KEY);
  localStorage.removeItem(SESSION_TOKEN_KEY);
}

function getStoredSession(): { username: string; sessionToken: string } | null {
  const username = localStorage.getItem(SESSION_USERNAME_KEY)?.trim() ?? '';
  const sessionToken = localStorage.getItem(SESSION_TOKEN_KEY)?.trim() ?? '';
  if (!username || !sessionToken) {
    return null;
  }
  return { username, sessionToken };
}

function loadStoredClass(): ClassType {
  const storedClass = localStorage.getItem(SELECTED_CLASS_KEY);
  return Object.values(ClassType).includes(storedClass as ClassType)
    ? storedClass as ClassType
    : ClassType.Warrior;
}

function loadStoredBotFill(): boolean {
  const stored = localStorage.getItem(BOT_FILL_KEY);
  if (stored === null) {
    return true;
  }
  return stored !== 'false';
}

function getAuthHeaders(): HeadersInit {
  const storedSession = getStoredSession();
  return storedSession ? { 'x-session-token': storedSession.sessionToken } : {};
}

function attemptStoredSessionRestore(): void {
  const storedSession = getStoredSession();
  if (!storedSession) {
    resetUiForLogin();
    updateLoginButton();
    return;
  }

  usernameInput.value = storedSession.username;
  authBusy = true;
  sessionRestoreInFlight = true;
  updateLoginButton();
  net.connect();
}

function showLobby(): void {
  canvas.hidden = true;
  hud.style.display = 'none';
  lobbyScreen.style.display = 'flex';
  updateMobileTargetStatus();
  updateLobbyUI();
}

function updateLobbyUI(): void {
  if (!myPlayerData) return;

  playerInfo.innerHTML = `
    <span class="hero-icon">${classIcons[myPlayerData.classType]}</span>
    <div>
      <strong>${myPlayerData.name}</strong>
      <span>${classLabels[myPlayerData.classType]} specialist</span>
    </div>
  `;

  const totalGames = Math.max(0, myPlayerData.matchesPlayed || (myPlayerData.wins + myPlayerData.losses));
  const winRate = totalGames === 0 ? 0 : (myPlayerData.wins / totalGames) * 100;
  const kd = myPlayerData.totalDeaths === 0
    ? myPlayerData.totalKills
    : myPlayerData.totalKills / myPlayerData.totalDeaths;

  playerStats.innerHTML = `
    <article class="stat-card accent-rating">
      <span class="stat-label">Rating</span>
      <strong>${myPlayerData.rating}</strong>
      <small>Best ${myPlayerData.bestRating}</small>
    </article>
    <article class="stat-card accent-record">
      <span class="stat-label">Record</span>
      <strong>${myPlayerData.wins}-${myPlayerData.losses}</strong>
      <small>${winRate.toFixed(1)}% win rate</small>
    </article>
    <article class="stat-card accent-economy">
      <span class="stat-label">Currency</span>
      <strong>${myPlayerData.currency}</strong>
      <small>${totalGames} matches played</small>
    </article>
    <article class="stat-card accent-damage">
      <span class="stat-label">Damage</span>
      <strong>${formatCompact(myPlayerData.totalDamage)}</strong>
      <small>Healing ${formatCompact(myPlayerData.totalHealing)}</small>
    </article>
    <article class="stat-card accent-combat">
      <span class="stat-label">Combat</span>
      <strong>${myPlayerData.totalKills}/${myPlayerData.totalDeaths}</strong>
      <small>${kd.toFixed(2)} K/D</small>
    </article>
    <article class="stat-card accent-class">
      <span class="stat-label">Class</span>
      <strong>${classLabels[myPlayerData.classType]}</strong>
      <small>${classDescriptions[myPlayerData.classType]}</small>
    </article>
  `;

  renderClassPreview(myPlayerData.classType);
}

function updateQueueUI(data: QueueUpdateMessage): void {
  queueSpinner.classList.toggle('active', data.queued);
  queue1v1Btn.classList.toggle('queued', data.queued && data.mode === GameMode.Arena1v1);
  queue2v2Btn.classList.toggle('queued', data.queued && data.mode === GameMode.Arena2v2);
  botFillToggle.disabled = data.queued;

  queue1v1Btn.textContent = data.queued && data.mode === GameMode.Arena1v1 ? 'Cancel 1v1' : 'Queue 1v1';
  queue2v2Btn.textContent = data.queued && data.mode === GameMode.Arena2v2 ? 'Cancel 2v2' : 'Queue 2v2';

  if (!data.queued) {
    queueSpinner.textContent = 'Ready to queue';
    return;
  }

  const queueLabel = data.mode === GameMode.Arena2v2
    ? 'Building a 2v2 lobby...'
    : 'Searching for an opponent...';
  if (!data.botFallbackEnabled) {
    queueSpinner.textContent = `${queueLabel} Bot fallback disabled. Waiting for players only.`;
    return;
  }

  queueSpinner.textContent = data.estimatedWait > 0
    ? `${queueLabel} Easy bot in ${data.estimatedWait}s if nobody joins.`
    : 'Launching practice match...';
}

function updateLobbyPresence(data: LobbyStatusMessage): void {
  const queue1v1Players = data.players.filter((player) => player.status === GameMode.Arena1v1);
  const queue2v2Players = data.players.filter((player) => player.status === GameMode.Arena2v2);

  onlineCount.textContent = String(data.players.length);
  queue1v1Count.textContent = String(queue1v1Players.length);
  queue2v2Count.textContent = String(queue2v2Players.length);

  if (data.players.length === 0) {
    presenceTableBody.innerHTML = '<tr><td colspan="3" class="presence-table-empty">Nobody here right now.</td></tr>';
    return;
  }

  presenceTableBody.innerHTML = data.players.map((player) => `
    <tr>
      <td>${player.name}</td>
      <td>${classLabels[player.classType]}</td>
      <td><span class="presence-status ${presenceStatusClass(player.status)}">${presenceStatusLabel(player.status)}</span></td>
    </tr>
  `).join('');
}

function presenceStatusLabel(status: 'online' | GameMode): string {
  switch (status) {
    case GameMode.Arena1v1:
      return 'Queueing 1v1';
    case GameMode.Arena2v2:
      return 'Queueing 2v2';
    default:
      return 'Online';
  }
}

function presenceStatusClass(status: 'online' | GameMode): string {
  switch (status) {
    case GameMode.Arena1v1:
      return 'queue-1v1';
    case GameMode.Arena2v2:
      return 'queue-2v2';
    default:
      return 'online';
  }
}

function setCurrentTarget(targetId: string | null): void {
  const me = currentState?.players[myPlayerId];
  if (me) {
    me.targetId = targetId;
    const target = targetId ? currentState?.players[targetId] ?? null : null;
    if (target && target.team !== me.team) {
      me.autoAttackTargetId = target.id;
    } else {
      me.autoAttackTargetId = null;
      me.autoAttackCooldownMs = 0;
    }
  }
  net.send({ type: ClientMessageType.SetTarget, targetId });
  updateMobileTargetStatus();
}

function selectNearestTarget(kind: 'ally' | 'enemy'): void {
  if (!currentState || !myPlayerId) return;

  const me = currentState.players[myPlayerId];
  if (!me) return;

  const candidates = Object.values(currentState.players)
    .filter((player) => {
      if (!player.alive || player.id === myPlayerId) return false;
      return kind === 'enemy' ? player.team !== me.team : player.team === me.team;
    })
    .sort((left, right) => {
      const leftDistance = getDistanceSquared(me, left);
      const rightDistance = getDistanceSquared(me, right);
      return leftDistance - rightDistance;
    });

  if (candidates.length === 0) {
    showToast(kind === 'enemy' ? 'No enemy target available.' : 'No ally target available.', 'info');
    return;
  }

  setCurrentTarget(candidates[0].id);
}

function cycleEnemyTarget(direction: number): void {
  if (!currentState || !myPlayerId) return;

  const me = currentState.players[myPlayerId];
  if (!me) return;

  const enemies = Object.values(currentState.players)
    .filter((player) => player.alive && player.id !== myPlayerId && player.team !== me.team)
    .sort((left, right) => {
      const distanceDelta = getDistanceSquared(me, left) - getDistanceSquared(me, right);
      if (distanceDelta !== 0) {
        return distanceDelta;
      }
      return left.name.localeCompare(right.name);
    });

  if (enemies.length === 0) {
    showToast('No enemy target available.', 'info');
    return;
  }

  const currentIndex = enemies.findIndex((player) => player.id === me.targetId);
  if (currentIndex === -1) {
    setCurrentTarget(enemies[0].id);
    return;
  }

  const nextIndex = (currentIndex + (direction >= 0 ? 1 : -1) + enemies.length) % enemies.length;
  setCurrentTarget(enemies[nextIndex].id);
}

function getDistanceSquared(from: PlayerState, to: PlayerState): number {
  const dx = from.position.x - to.position.x;
  const dz = from.position.z - to.position.z;
  return dx * dx + dz * dz;
}

function updateMobileTargetStatus(): void {
  mobileTargetEnemy.classList.remove('active');
  mobileTargetAlly.classList.remove('active');
  mobileTargetSelf.classList.remove('active');

  if (!currentState || !currentState.players[myPlayerId]) {
    mobileTargetStatus.textContent = 'Tap arena or choose target';
    return;
  }

  const me = currentState.players[myPlayerId];
  const targetId = me.targetId;
  if (!targetId) {
    mobileTargetStatus.textContent = 'Tap arena or choose target';
    return;
  }

  if (targetId === myPlayerId) {
    mobileTargetStatus.textContent = 'Self selected';
    mobileTargetSelf.classList.add('active');
    return;
  }

  const target = currentState.players[targetId];
  if (!target) {
    mobileTargetStatus.textContent = 'Tap arena or choose target';
    return;
  }

  if (target.team === me.team) {
    mobileTargetStatus.textContent = `Ally: ${target.name}`;
    mobileTargetAlly.classList.add('active');
    return;
  }

  mobileTargetStatus.textContent = `Enemy: ${target.name}`;
  mobileTargetEnemy.classList.add('active');
}

async function loadLeaderboard(): Promise<void> {
  try {
    const response = await fetch('/api/leaderboard?limit=25');
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const entries = await response.json() as LeaderboardEntry[];
    leaderboardBody.innerHTML = entries.map((entry) => {
      const isSelf = myPlayerData?.name === entry.name;
      return `
        <tr class="${isSelf ? 'is-self' : ''}">
          <td>${entry.rank}</td>
          <td>${entry.name}</td>
          <td>${classLabels[entry.classType]}</td>
          <td>${entry.rating}</td>
          <td>${entry.bestRating}</td>
          <td>${entry.winRate.toFixed(1)}%</td>
        </tr>
      `;
    }).join('');
  } catch (error) {
    console.error('Failed to load leaderboard:', error);
    leaderboardBody.innerHTML = '<tr><td colspan="6">Leaderboard unavailable.</td></tr>';
  }
}

function renderClassPreview(classType: ClassType): void {
  const abilities = getClassAbilities(classType);
  const playstyleTags = getClassPlaystyleTags(classType);
  const playstyleTip = getClassPlaystyleTip(classType);
  const previewMarkup = `
    <div class="class-preview-card class-${classType}">
      <div class="class-preview-head">
        <span class="class-preview-icon">${classIcons[classType]}</span>
        <div class="class-preview-copy">
          <h3>${classLabels[classType]}</h3>
          <p>${classDescriptions[classType]}</p>
        </div>
      </div>
      <div class="class-preview-tags">
        ${playstyleTags.map((tag) => `<span class="preview-pill">${tag}</span>`).join('')}
      </div>
      <div class="class-preview-abilities">
        ${abilities.map((ability, index) => `
          <article class="summary-ability">
            <span>${abilityIcons[ability.id] || '?'}</span>
            <strong>${index + 1}. ${ability.name}</strong>
          </article>
        `).join('')}
      </div>
      <p class="class-preview-tip">${playstyleTip}</p>
    </div>
  `;

  loginClassPreview.innerHTML = previewMarkup;
  classKitModalTitle.textContent = `${classLabels[classType]} abilities`;
  classKitModalSubtitle.textContent = classType === ClassType.Priest
    ? 'Priest note: Heal and Power Word: Shield self-cast if no friendly target is selected.'
    : 'Every spell is live. Use the popup when you need the full reference without stretching the main UI.';
  classKitModalList.innerHTML = abilities.map((ability, index) => `
    <article class="kit-detail-card">
      <div class="kit-detail-top">
        <div class="ability-preview-top">
          <span class="ability-preview-key">${index + 1}</span>
          <span class="ability-preview-name">${ability.name}</span>
        </div>
        <span class="ability-preview-icon">${abilityIcons[ability.id] || '?'}</span>
      </div>
      <div class="kit-detail-meta">
        <span>${targetLabel(ability.targetType)}</span>
        <span>${rangeLabel(ability.range)}</span>
        <span>${castLabel(ability.castTime)}</span>
      </div>
      <p>${ability.description}</p>
    </article>
  `).join('');
}

function getClassPlaystyleTags(classType: ClassType): string[] {
  switch (classType) {
    case ClassType.Warrior:
      return ['Melee pressure', 'Gap close', 'Execute finish'];
    case ClassType.Mage:
      return ['Range control', 'Burst windows', 'Mobility'];
    case ClassType.Priest:
      return ['Sustain', 'Shields', 'Attrition'];
    default:
      return ['Arena', 'PvP', 'Combat'];
  }
}

function getClassPlaystyleTip(classType: ClassType): string {
  switch (classType) {
    case ClassType.Warrior:
      return 'Stick to a single target, keep them slowed, and spend rage on Mortal Strike or Execute when they dip low.';
    case ClassType.Mage:
      return 'Create space with Frost Nova or Blink, then land Fireball while the target is locked down or polymorphed.';
    case ClassType.Priest:
      return 'Trade damage and healing patiently, use shields before pressure lands, and fear when enemies collapse on you.';
    default:
      return 'Pick a class and learn the full five-spell kit before you queue.';
  }
}

function showClassKitPanel(): void {
  classKitPanel.style.display = 'flex';
}

function targetLabel(targetType: TargetType): string {
  switch (targetType) {
    case TargetType.Enemy:
      return 'Enemy target';
    case TargetType.Friendly:
      return 'Friendly or self';
    case TargetType.Self:
      return 'Self only';
    default:
      return 'Targeted';
  }
}

function rangeLabel(range: number): string {
  return range === 0 ? 'Self' : `${range} yd`;
}

function castLabel(castTime: number): string {
  return castTime === 0 ? 'Instant' : `${castTime.toFixed(1)}s cast`;
}

function setupAbilityBar(abilities: { id: AbilityId; name: string }[]): void {
  abilityBar.innerHTML = abilities.map((ability, index) => {
    const definition = ABILITIES[ability.id];
    const title = `${ability.name} - ${definition.description}`;
    return `
      <button
        class="ability-btn"
        id="ability-${ability.id}"
        data-ability-id="${ability.id}"
        title="${title}"
        type="button"
      >
        <span class="key">${index + 1}</span>
        <span class="icon">${abilityIcons[ability.id] || '?'}</span>
        <span class="ability-label">${ability.name}</span>
      </button>
    `;
  }).join('');
}

function getAbilityButtonFromEventTarget(target: EventTarget | null): HTMLElement | null {
  return target instanceof HTMLElement ? target.closest<HTMLElement>('.ability-btn') : null;
}

function sendAbilityFromButton(abilityButton: HTMLElement): void {
  const abilityId = abilityButton.dataset.abilityId as AbilityId | undefined;
  if (!abilityId) return;
  net.send({ type: ClientMessageType.UseAbility, abilityId });
}

function showTalentsPanel(): void {
  const panel = document.getElementById('talents-panel')!;
  panel.style.display = 'flex';

  const currencyEl = document.getElementById('talent-currency')!;
  currencyEl.textContent = `Currency: ${myPlayerData?.currency || 0}`;

  const listEl = document.getElementById('talent-list')!;
  const talents = getClassTalents(getActiveClass());

  listEl.innerHTML = talents.map((talent) => {
    const currentRank = myPlayerData?.talents[talent.id] || 0;
    const nextCost = currentRank < talent.maxRank ? talent.costs[currentRank] : null;
    const canBuy = nextCost !== null && (myPlayerData?.currency || 0) >= nextCost;
    const bonus = currentRank > 0 ? `+${talent.bonusPerRank[currentRank - 1]}%` : 'No rank';

    return `
      <div class="talent-row">
        <div class="talent-info">
          <h4>${talent.name}</h4>
          <p>${talent.description}</p>
          <span class="talent-ranks">${currentRank}/${talent.maxRank} • ${bonus}${nextCost ? ` • Next ${nextCost}` : ' • MAX'}</span>
        </div>
        <div class="talent-actions">
          <button onclick="buyTalent('${talent.id}')" ${!canBuy ? 'disabled' : ''}>Buy</button>
          <button onclick="refundTalent('${talent.id}')" ${currentRank === 0 ? 'disabled' : ''}>Refund</button>
        </div>
      </div>
    `;
  }).join('');
}

const talentActionsWindow = window as Window & typeof globalThis & {
  buyTalent: (talentId: string) => Promise<void>;
  refundTalent: (talentId: string) => Promise<void>;
};

talentActionsWindow.buyTalent = async (talentId: string) => {
  try {
    const response = await fetch('/api/talents/buy', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify({ talentId }),
    });
    const data = await response.json();
    if (response.status === 401) {
      clearStoredSession();
      resetUiForLogin();
      net.disconnect();
      showToast('Session expired. Sign in again.', 'error');
      return;
    }
    if (!response.ok || !myPlayerData) {
      showToast(data.error || 'Unable to buy talent.', 'error');
      return;
    }

    myPlayerData.currency = data.currency;
    myPlayerData.talents = data.talents;
    showTalentsPanel();
    updateLobbyUI();
  } catch (error) {
    console.error(error);
    showToast('Talent purchase failed.', 'error');
  }
};

talentActionsWindow.refundTalent = async (talentId: string) => {
  try {
    const response = await fetch('/api/talents/refund', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify({ talentId }),
    });
    const data = await response.json();
    if (response.status === 401) {
      clearStoredSession();
      resetUiForLogin();
      net.disconnect();
      showToast('Session expired. Sign in again.', 'error');
      return;
    }
    if (!response.ok || !myPlayerData) {
      showToast(data.error || 'Unable to refund talent.', 'error');
      return;
    }

    myPlayerData.currency = data.currency;
    myPlayerData.talents = data.talents;
    showTalentsPanel();
    updateLobbyUI();
  } catch (error) {
    console.error(error);
    showToast('Talent refund failed.', 'error');
  }
};

async function refreshPlayerProfile(): Promise<void> {
  if (!myPlayerId) return;

  try {
    const response = await fetch('/api/player', {
      headers: getAuthHeaders(),
    });
    if (response.status === 401) {
      clearStoredSession();
      resetUiForLogin();
      net.disconnect();
      showToast('Session expired. Sign in again.', 'error');
      return;
    }
    if (!response.ok) return;
    myPlayerData = await response.json() as PlayerData;
    setSelectedClass(myPlayerData.classType, true);
    updateLobbyUI();
  } catch (error) {
    console.error('Failed to refresh profile:', error);
  }
}

function updateHUD(): void {
  if (!currentState || !currentState.players[myPlayerId]) return;

  const me = currentState.players[myPlayerId];
  document.getElementById('self-name')!.textContent = `${me.name} (${classLabels[me.classType]})`;

  const selfHp = document.getElementById('self-hp')! as HTMLElement;
  selfHp.style.width = `${(me.hp / me.maxHp) * 100}%`;
  document.getElementById('self-hp-text')!.textContent = `${Math.ceil(me.hp)} / ${me.maxHp}`;

  const selfResource = document.getElementById('self-resource')! as HTMLElement;
  selfResource.style.width = `${(me.resource / me.maxResource) * 100}%`;
  selfResource.className = `fill ${me.resourceType}`;
  document.getElementById('self-resource-text')!.textContent = `${Math.ceil(me.resource)} / ${me.maxResource}`;
  updateAutoAttackStatus(me);
  updateBuffBar('self-buffs', me);

  const targetFrame = document.getElementById('target-frame')!;
  if (me.targetId && currentState.players[me.targetId]) {
    const target = currentState.players[me.targetId];
    targetFrame.style.display = 'block';
    document.getElementById('target-name')!.textContent = `${target.name} (${classLabels[target.classType]})`;

    const targetHp = document.getElementById('target-hp')! as HTMLElement;
    targetHp.style.width = `${(target.hp / target.maxHp) * 100}%`;
    document.getElementById('target-hp-text')!.textContent = `${Math.ceil(target.hp)} / ${target.maxHp}`;

    const targetResource = document.getElementById('target-resource')! as HTMLElement;
    targetResource.style.width = `${(target.resource / target.maxResource) * 100}%`;
    targetResource.className = `fill ${target.resourceType}`;
    document.getElementById('target-resource-text')!.textContent = `${Math.ceil(target.resource)} / ${target.maxResource}`;
    updateBuffBar('target-buffs', target);
  } else {
    targetFrame.style.display = 'none';
  }

  const castBar = document.getElementById('cast-bar')!;
  if (me.castingAbility) {
    castBar.style.display = 'block';
    document.getElementById('cast-label')!.textContent = ABILITIES[me.castingAbility].name;
    (document.getElementById('cast-fill')! as HTMLElement).style.width = `${me.castProgress * 100}%`;
  } else {
    castBar.style.display = 'none';
  }

  updateAbilityCooldowns(me);

  const elapsed = Math.floor(currentState.timeElapsed / 1000);
  const minutes = Math.floor(elapsed / 60);
  const seconds = elapsed % 60;
  document.getElementById('match-timer')!.textContent = `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

function updateAutoAttackStatus(player: PlayerState): void {
  if (!currentState) {
    selfAutoAttackStatus.textContent = 'Auto attack idle';
    selfAutoAttackStatus.className = 'auto-attack-status idle';
    return;
  }

  const target = player.autoAttackTargetId ? currentState.players[player.autoAttackTargetId] ?? null : null;
  if (!target) {
    selfAutoAttackStatus.textContent = 'Auto attack idle';
    selfAutoAttackStatus.className = 'auto-attack-status idle';
    return;
  }

  const profile = AUTO_ATTACKS[player.classType];
  const autoError = canAutoAttack(player, target, currentState.pillars);
  if (autoError === null) {
    const swingSeconds = Math.max(0, player.autoAttackCooldownMs) / 1000;
    const swingLabel = swingSeconds > 0 ? `${swingSeconds.toFixed(1)}s` : 'ready';
    selfAutoAttackStatus.textContent = `${profile.type === 'melee' ? 'Auto swing' : 'Auto shot'}: ${target.name} • ${swingLabel}`;
    selfAutoAttackStatus.className = 'auto-attack-status active';
    return;
  }

  selfAutoAttackStatus.textContent = `${profile.type === 'melee' ? 'Auto swing' : 'Auto shot'} primed: ${target.name} • ${autoError.toLowerCase()}`;
  selfAutoAttackStatus.className = 'auto-attack-status pending';
}

function updateBuffBar(elementId: string, player: PlayerState): void {
  const container = document.getElementById(elementId)!;
  const debuffTypes = [
    BuffType.Root,
    BuffType.Slow,
    BuffType.MortalWound,
    BuffType.Polymorph,
    BuffType.Fear,
    BuffType.ShadowWordPain,
  ];

  container.innerHTML = player.buffs.map((buff) => {
    const seconds = Math.ceil(buff.remainingMs / 1000);
    const labels: Record<string, string> = {
      root: 'R',
      slow: 'S',
      mortal_wound: 'M',
      polymorph: 'P',
      fear: 'F',
      shadow_word_pain: 'DoT',
      shield_block: 'SB',
      ice_barrier: 'IB',
      power_word_shield: 'PW',
    };
    return `<div class="buff-icon ${debuffTypes.includes(buff.type) ? 'debuff' : 'buff'}" title="${buff.type} (${seconds}s)">${labels[buff.type] || '?'}</div>`;
  }).join('');
}

function updateAbilityCooldowns(player: PlayerState): void {
  const abilities = getClassAbilities(player.classType);
  for (const ability of abilities) {
    const element = document.getElementById(`ability-${ability.id}`);
    if (!element) continue;

    const cooldown = player.abilityCooldowns[ability.id] || 0;
    const remaining = Math.max(cooldown, player.globalCooldown);
    let overlay = element.querySelector<HTMLElement>('.cd-overlay');
    const targetId = player.targetId;
    const target = targetId ? currentState?.players[targetId] ?? null : null;
    const effectiveTarget = target || (ability.targetType !== TargetType.Enemy ? player : null);
    const castError = currentState
      ? canUseAbility(player, ability.id, effectiveTarget, currentState.players, currentState.pillars)
      : 'No state';
    const castable = castError === null;

    if (remaining > 0) {
      element.classList.add('on-cd');
      if (!overlay) {
        overlay = document.createElement('div');
        overlay.className = 'cd-overlay';
        element.appendChild(overlay);
      }
      overlay.textContent = formatCooldownSeconds(remaining);
    } else {
      element.classList.remove('on-cd');
      overlay?.remove();
    }

    element.classList.toggle('ready', castable && remaining <= 0);
    element.classList.toggle('blocked', !castable && remaining <= 0);
    element.setAttribute('aria-disabled', castable ? 'false' : 'true');
    element.title = `${ability.name} - ${castError ?? 'Ready'}`;
  }
}

function formatCooldownSeconds(remainingMs: number): string {
  const seconds = remainingMs / 1000;
  if (seconds >= 10) {
    return String(Math.ceil(seconds));
  }
  return seconds.toFixed(1);
}

function handleCombatEvent(event: CombatEvent): void {
  if (!currentState) return;

  const worldPosition = renderer.getWorldPosition(event.targetId);
  if (!worldPosition) return;

  const screenPosition = renderer.projectToScreen(worldPosition);
  if (!screenPosition) return;

  let text = '';
  let className = 'damage';

  switch (event.type) {
    case CombatEventType.Damage:
      text = `-${Math.round(event.amount || 0)}`;
      className = 'damage';
      break;
    case CombatEventType.Heal:
      text = `+${Math.round(event.amount || 0)}`;
      className = 'heal';
      break;
    case CombatEventType.Absorb:
      text = `Absorb ${Math.round(event.amount || 0)}`;
      className = 'absorb';
      break;
    case CombatEventType.Immune:
      text = 'IMMUNE';
      className = 'immune';
      break;
    case CombatEventType.Death:
      text = 'KILLED';
      className = 'damage';
      break;
    default:
      return;
  }

  spawnFloatingText(text, className, screenPosition.x, screenPosition.y);
}

function spawnFloatingText(text: string, className: string, x: number, y: number): void {
  const element = document.createElement('div');
  element.className = `combat-text ${className}`;
  element.textContent = text;
  element.style.left = `${x + (Math.random() - 0.5) * 40}px`;
  element.style.top = `${y - 30}px`;
  document.getElementById('app')!.appendChild(element);

  setTimeout(() => element.remove(), 1500);
}

function showToast(message: string, tone: 'error' | 'info' = 'info'): void {
  toast.textContent = message;
  toast.className = `status-toast visible ${tone}`;

  if (toastTimer) {
    clearTimeout(toastTimer);
  }

  toastTimer = setTimeout(() => {
    toast.className = 'status-toast';
  }, 2400);
}

function formatCompact(value: number): string {
  return new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(value);
}

function gameLoop(): void {
  if (inMatch && currentState) {
    renderer.updateState(currentState, myPlayerId, input.getCameraYaw());
    updateHUD();
    renderer.render();
  }

  requestAnimationFrame(gameLoop);
}

requestAnimationFrame(gameLoop);
setSelectedClass(selectedClass, true);
updateQueueUI({ queued: false, mode: null, estimatedWait: 0, type: ServerMessageType.QueueUpdate });
updateLobbyPresence({
  type: ServerMessageType.LobbyStatus,
  players: [],
});
updateMobileTargetStatus();
attemptStoredSessionRestore();
